import type { Action, Thunk, Computed } from 'easy-peasy';
import { action, thunk, computed } from 'easy-peasy';
import type { StoreModel } from '..';
import type { Injections } from '../services/injections';
import Cookies from 'universal-cookie';
import type { ApplicationErrorOptions } from '../../types';

const FIVE_YEARS_IN_SECONDS = 60 * 60 * 24 * 365 * 5;

export interface PrintZPLOptions {
  printerName: string;
  zpl: string;
}

export interface QzPrinterEvent {
  printerName: string;
  jobName: string;
  eventType: string;
  statusText: string;
  severity: string;
  statusCode: string;
  message: string;
}

export interface QzTrayModel {
  qzTrayConnected: boolean;
  lastEvent?: QzPrinterEvent;
  defaultPrinter: Computed<QzTrayModel, () => string | undefined>;
  printers?: string[];
  fetchPrinters: Thunk<QzTrayModel, undefined, Injections, StoreModel, Promise<string[] | undefined>>;
  qzTrayConnect: Thunk<QzTrayModel, undefined, Injections, StoreModel, Promise<any>>;
  setPrinters: Action<QzTrayModel, string[]>;
  setLastPrinterEvent: Action<QzTrayModel, QzPrinterEvent>;
  qzTrayActionFailure: Action<QzTrayModel, ApplicationErrorOptions>;
  setDefaultPrinter: Action<QzTrayModel, string>;
  setQzTrayConnected: Action<QzTrayModel, boolean>;
  printZPL: Thunk<QzTrayModel, PrintZPLOptions, Injections, StoreModel, Promise<null>>;
}

export const DEFAULT_ZPL_PRINTER_COOKIE_NAME = 'zplprinter';
export const cookies = new Cookies();
export const getDefaultZplPrinterName = () => cookies.get(DEFAULT_ZPL_PRINTER_COOKIE_NAME);

export const qzTrayModel: QzTrayModel = {
  qzTrayConnected: false,
  defaultPrinter: computed((state) => () => {
    const defaultPrinterName = getDefaultZplPrinterName();

    if (defaultPrinterName) {
      return defaultPrinterName;
    }

    if (state.printers && state.printers.length === 1) {
      return state.printers[0];
    }
  }),
  qzTrayActionFailure: action((state, payload) => {
    console.log(payload);
  }),
  qzTrayConnect: thunk(async (actions, payload, { injections }) => {
    const { authService } = injections;
    const qz = (window as any).qz;
    if (!qz.websocket.isActive()) {
      qz.security.setCertificatePromise((resolve: any, reject: any) => {
        authService
          .fetchQzCert()
          .then((data) => resolve(data))
          .catch(() => reject(''));
      });
      qz.security.setSignatureAlgorithm('SHA512');
      qz.security.setSignaturePromise((toSign: string) => {
        return (resolve: any, reject: any) => {
          authService
            .qzSign(toSign)
            .then((data) => resolve(data))
            .catch(() => reject(''));
        };
      });
      qz.printers.setPrinterCallbacks((qzEvent: QzPrinterEvent) => {
        actions.setLastPrinterEvent(qzEvent);
      });

      await qz.websocket.connect().then(() => {
        qz.printers.startListening().then(() => {
          return qz.printers.getStatus();
        });
      });
    }
    actions.setQzTrayConnected(true);
    return qz;
  }),
  fetchPrinters: thunk(async (actions) => {
    try {
      const qz = await actions.qzTrayConnect();
      const printers = (await qz.printers.find()) as string[];
      actions.setPrinters(printers);
      if (printers.length === 1) {
        actions.setDefaultPrinter(printers[0]);
      }
      return printers;
    } catch (error) {
      console.log(error);
      return undefined;
    }
  }),
  printZPL: thunk(async (actions, payload) => {
    try {
      const qz = await actions.qzTrayConnect();
      actions.setQzTrayConnected(true);
      const printer = qz.configs.create(payload.printerName);
      await qz.print(printer, [payload.zpl]);
    } catch (error) {
      actions.qzTrayActionFailure({
        error,
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  setPrinters: action((state, payload) => {
    state.printers = payload || [];
  }),
  setDefaultPrinter: action((state, payload) => {
    cookies.set(DEFAULT_ZPL_PRINTER_COOKIE_NAME, payload, { path: '/', maxAge: FIVE_YEARS_IN_SECONDS });
  }),
  setQzTrayConnected: action((state, connected) => {
    state.qzTrayConnected = connected;
  }),
  setLastPrinterEvent: action((state, payload) => {
    state.lastEvent = payload;
  }),
};
